import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Check, Play, Square, Loader2, Save } from "lucide-react";
import AppShell from "@/components/AppShell";

/**
 * CHANGE CHARACTER — explicit select → preview → confirm flow (Iter 123).
 *
 * Tapping a card only changes the LOCAL selection (highlight). Voice preview
 * plays per character (switching characters stops any playing preview).
 * The change is committed ONLY via the "Save Changes" button; leaving the
 * screen without saving persists nothing. Both companions are available on
 * the Free plan — Premium is about longer sessions and richer atmospheres,
 * never the character.
 */
const PREVIEW_TEXT_BY_CHARACTER = {
  seriny: "Hello, darling. [pause] Nice to see you again.",
  elizabeth: "Hello, dear. [pause] Welcome home.",
};

export default function Characters() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewId, setPreviewId] = useState(null); // character whose voice is loading/playing
  const [previewLoading, setPreviewLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/catalog/characters").then((r) => setCharacters(r.data || []));
  }, []);

  // Initial state: the currently active character is selected.
  useEffect(() => {
    if (user?.character_id) setSelectedId((prev) => prev ?? user.character_id);
  }, [user]);

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
    }
    setPreviewId(null);
    setPreviewLoading(false);
  };

  useEffect(() => stopPreview, []);

  const selectCharacter = (id) => {
    if (id !== selectedId) stopPreview(); // never overlap previews across characters
    setSelectedId(id);
  };

  const togglePreview = async (id) => {
    if (previewId === id && !previewLoading) {
      stopPreview();
      return;
    }
    stopPreview();
    setSelectedId(id);
    setPreviewId(id);
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/tts/synthesize", {
        character_id: id,
        text: PREVIEW_TEXT_BY_CHARACTER[id] || "Hello.",
      });
      const filename = data?.filename || (data?.hash ? `${data.hash}.mp3` : null);
      if (!filename) throw new Error("No audio returned.");
      if (audioRef.current) {
        audioRef.current.src = `${API}/audio/${filename}`;
        await audioRef.current.play();
      }
      setPreviewLoading(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Voice preview unavailable just now.";
      toast.error(typeof msg === "string" ? msg : msg?.message || "Voice preview unavailable just now.");
      stopPreview();
    }
  };

  const hasChanges = !!selectedId && selectedId !== user?.character_id;

  const saveChanges = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    stopPreview();
    try {
      await api.patch("/profile/companion", { character_id: selectedId });
      await refresh();
      const name = characters.find((c) => c.id === selectedId)?.name || "them";
      toast.success(`You'll spend tonight with ${name}.`);
      nav(-1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Could not switch.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell withBottomNav={false}>
      <div className="max-w-md mx-auto px-6 pb-14">
        <button
          type="button"
          onClick={() => nav(-1)}
          data-testid="characters-back-button"
          aria-label="Back without saving"
          className="mb-4 p-2 -ml-2 text-violet-200/70 hover:text-violet-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <h1 className="font-serif-display text-3xl mb-2 text-violet-50/95">Change Character</h1>
        <p className="text-xs text-violet-300/55 mb-6 leading-relaxed">
          Each companion remembers your story separately — switching never erases the time you've spent together.
        </p>

        <div className="space-y-4" data-testid="characters-list">
          {characters.map((c) => {
            const isActive = user?.character_id === c.id;
            const isSelected = selectedId === c.id;
            const isPreviewing = previewId === c.id;
            return (
              <article
                key={c.id}
                onClick={() => selectCharacter(c.id)}
                data-testid={`character-card-${c.id}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") selectCharacter(c.id); }}
                aria-pressed={isSelected}
                className={`relative aspect-square w-full rounded-2xl border overflow-hidden transition-all cursor-pointer ${
                  isSelected
                    ? "border-violet-200/60 bg-violet-200/8 shadow-[0_0_28px_rgba(196,181,253,0.14)]"
                    : "border-violet-200/15 bg-violet-200/4 hover:border-violet-200/30"
                }`}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(8,7,22,0.0) 35%, rgba(8,7,22,0.78) 100%), url("${c.image}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center top",
                  }}
                  aria-hidden="true"
                />
                {isSelected && (
                  <span
                    className="absolute top-4 right-4 z-10 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-violet-50 bg-violet-950/60 border border-violet-200/45 rounded-full px-3 py-1.5 backdrop-blur-sm"
                    data-testid={`character-card-${c.id}-selected`}
                  >
                    <Check className="w-3 h-3" strokeWidth={2} /> Selected
                  </span>
                )}
                <div className="relative z-10 h-full flex flex-col justify-end p-5">
                  <h2
                    className="font-serif-display text-2xl text-violet-50"
                    data-testid={`character-card-${c.id}-name`}
                  >
                    {c.name}
                  </h2>
                  {isActive && (
                    <span
                      className="mt-1 text-[10px] tracking-[0.28em] uppercase text-violet-200/70"
                      data-testid={`character-card-${c.id}-active`}
                    >
                      Current
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); togglePreview(c.id); }}
                    data-testid={`character-card-${c.id}-preview`}
                    aria-label={isPreviewing ? `Stop ${c.name}'s voice preview` : `Play ${c.name}'s voice preview`}
                    className={`mt-3 inline-flex items-center gap-2 self-start px-4 py-2 rounded-full border text-xs transition-colors ${
                      isPreviewing
                        ? "border-violet-200/55 bg-violet-200/14 text-violet-50"
                        : "border-violet-200/25 bg-violet-950/40 text-violet-100/85 hover:bg-violet-200/10 hover:border-violet-200/40"
                    }`}
                  >
                    {isPreviewing && previewLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                    ) : isPreviewing ? (
                      <Square className="w-3 h-3" strokeWidth={1.5} />
                    ) : (
                      <Play className="w-3.5 h-3.5 translate-x-[1px]" strokeWidth={1.5} />
                    )}
                    <span>{isPreviewing && !previewLoading ? "Playing…" : "Voice preview"}</span>
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        <audio
          ref={audioRef}
          onEnded={stopPreview}
          onError={() => { setPreviewId(null); setPreviewLoading(false); }}
          className="hidden"
          data-testid="characters-preview-audio"
        />

        {/* Save Changes — only commits on explicit confirmation. */}
        <div className="sticky mt-8 z-20" style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}>
          <button
            type="button"
            onClick={saveChanges}
            disabled={!hasChanges || saving}
            data-testid="characters-save-button"
            className={`w-full flex items-center justify-center gap-2 rounded-full border px-6 py-3.5 text-sm transition-all backdrop-blur-md ${
              hasChanges && !saving
                ? "border-violet-200/45 bg-violet-300/16 text-violet-50 hover:border-violet-100/60 hover:bg-violet-300/22 active:scale-[0.99] shadow-[0_0_24px_rgba(167,139,250,0.18)]"
                : "border-violet-200/15 bg-violet-200/5 text-violet-300/45 cursor-not-allowed"
            }`}
          >
            <Save className="w-4 h-4" strokeWidth={1.5} />
            {saving ? "Saving…" : hasChanges ? "Save Changes" : "No changes to save"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
